import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import { addNotification } from '../../../store/slices/uiSlice';
import './RFQs.css';

const RFQs: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  // RFQs State from Local Storage
  const [rfqList, setRfqList] = useState<any[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Form Fields
  const [prodName, setProdName] = useState('');
  const [qty, setQty] = useState('');
  const [targetPrice, setTargetPrice] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [remarks, setRemarks] = useState('');

  // Initial Seeder and Loader
  useEffect(() => {
    try {
      const saved = localStorage.getItem('user_rfqs');
      if (saved) {
        setRfqList(JSON.parse(saved));
      }
    } catch (e) {
      console.error(e);
    }
  }, [user]);

  // Handle Quote Submission
  const handleSubmitRFQ = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prodName.trim()) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: 'Product name is required!' }));
      return;
    }
    if (!qty || parseFloat(qty) <= 0) {
      dispatch(addNotification({ type: 'error', title: 'Error', message: 'Valid required quantity is required!' }));
      return;
    }

    const newRFQ = {
      id: `RFQ_${Math.floor(10000 + Math.random() * 90000)}`,
      productName: prodName,
      quantity: parseInt(qty),
      targetPrice: parseFloat(targetPrice) || 0,
      date: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
      expectedDate: expectedDate || 'ASAP',
      userName: user ? `${user.firstName} ${user.lastName}` : 'Buyer',
      userEmail: user?.email || 'user@industrybull.com',
      status: 'Pending Review',
      specialPrice: null,
      remarks: remarks
    };

    try {
      const updated = [newRFQ, ...rfqList];
      setRfqList(updated);
      localStorage.setItem('user_rfqs', JSON.stringify(updated));
      dispatch(addNotification({ type: 'success', title: 'Success', message: `RFQ "${prodName}" raised successfully! Wholesale price evaluation started.` }));
      
      // Reset Form
      setProdName('');
      setQty('');
      setTargetPrice('');
      setExpectedDate('');
      setRemarks('');
      setIsFormOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const trendingCategories = [
    { name: "Safety", image: "/assets/images/torch_light.png" },
    { name: "Machine", image: "/assets/images/sewing_machine.png" },
    { name: "Generator", image: "/assets/images/air_compressor.png" },
    { name: "Camera", image: "/assets/images/camera.png" },
    { name: "Cleaner", image: "/assets/images/vacuum_cleaner.png" },
    { name: "Stove", image: "/assets/images/induction_cooktop.png" }
  ];

  const fullName = user ? `${user.firstName} ${user.lastName}` : 'Guest User';

  return (
    <div className="account-rfqs-page">
      <div className="account-rfqs-container">
        
        <div className="account-layout">
          
          {/* Sidebar */}
          <aside className="account-sidebar-panel">
            <div className="account-user-header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="user-icon">
                <path d="M20 21a8 8 0 0 0-16 0"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
              {fullName}
            </div>
            <nav className="account-nav-list">
              <Link to="/account/addresses" className="account-nav-item">
                <span className="icon">⌖</span> My Addresses
              </Link>
              <Link to="/account/orders" className="account-nav-item">
                <span className="icon">▣</span> My Order
              </Link>
              <Link to="/account/rfqs" className="account-nav-item active">
                <span className="icon green-icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span> My RFQ's
              </Link>
              <Link to="/account/business" className="account-nav-item">
                <span className="icon">▤</span> My Business Details
              </Link>
              <Link to="/account/wishlist" className="account-nav-item">
                <span className="icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg></span> My Wishlist
              </Link>
              <Link to="/logout" className="account-nav-item">
                <span className="icon"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span> Log-out
              </Link>
            </nav>
          </aside>

          {/* Main Content */}
          <main className="account-main-content">
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h1 style={{ fontSize: '26px', fontWeight: '700', color: '#1e293b' }}>Request Wholesale Quote (RFQ)</h1>
                <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Request corporate bulk pricing for high-volume purchasing direct from manufacturers.</p>
              </div>
              <button 
                onClick={() => setIsFormOpen(!isFormOpen)} 
                className="start-shopping-btn"
                style={{ background: isFormOpen ? '#64748b' : '#df2027' }}
              >
                {isFormOpen ? 'Close Form' : 'Raise New RFQ +'}
              </button>
            </div>

            {/* Quote Submission Form */}
            {isFormOpen && (
              <form onSubmit={handleSubmitRFQ} style={{ background: '#f8fafc', padding: '24px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '30px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>Submit Bulk Pricing Inquiry</h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Product Description / Name *</label>
                    <input 
                      type="text" 
                      placeholder="e.g. Zicom Wi-Fi PTZ Camera (Pack of 50)" 
                      value={prodName}
                      onChange={(e) => setProdName(e.target.value)}
                      style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Required Quantity *</label>
                    <input 
                      type="number" 
                      placeholder="e.g. 50" 
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Target Price per Unit (₹, optional)</label>
                    <input 
                      type="number" 
                      placeholder="e.g. 1500" 
                      value={targetPrice}
                      onChange={(e) => setTargetPrice(e.target.value)}
                      style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Expected Delivery Date</label>
                    <input 
                      type="date" 
                      value={expectedDate}
                      onChange={(e) => setExpectedDate(e.target.value)}
                      style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', color: '#334155' }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Detailed Requirements / Technical Specifications</label>
                  <textarea 
                    placeholder="Provide compliance parameters, shipping specifications, or target budget parameters..." 
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    style={{ width: '100%', padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', minHeight: '80px', fontFamily: 'inherit' }}
                  />
                </div>

                <button 
                  type="submit" 
                  className="start-shopping-btn" 
                  style={{ background: '#df2027', width: '100%', padding: '12px', fontSize: '16px', borderRadius: '6px' }}
                >
                  Submit Quotation Inquiry
                </button>
              </form>
            )}

            {/* Submitted RFQ Dashboard */}
            {rfqList.length > 0 ? (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', marginBottom: '35px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                      <th style={{ padding: '15px 20px' }}>RFQ ID</th>
                      <th style={{ padding: '15px 20px' }}>Inquired Product</th>
                      <th style={{ padding: '15px 20px' }}>Quantity</th>
                      <th style={{ padding: '15px 20px' }}>Target Bid</th>
                      <th style={{ padding: '15px 20px' }}>Approved Special Price</th>
                      <th style={{ padding: '15px 20px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rfqList.map((r) => (
                      <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9', color: '#1e293b' }}>
                        <td style={{ padding: '15px 20px', fontWeight: '700' }}>{r.id}</td>
                        <td style={{ padding: '15px 20px', fontWeight: '600' }}>{r.productName}</td>
                        <td style={{ padding: '15px 20px', fontWeight: '600' }}>{r.quantity} Unit(s)</td>
                        <td style={{ padding: '15px 20px', color: '#64748b' }}>₹{r.targetPrice || 0}</td>
                        <td style={{ padding: '15px 20px', color: '#10b981', fontWeight: '800' }}>
                          {r.specialPrice ? `₹${r.specialPrice}/pc` : 'Under Review'}
                        </td>
                        <td style={{ padding: '15px 20px' }}>
                          <span style={{ 
                            color: r.status.includes('Approved') || r.status.includes('Offered') ? '#10b981' : '#f59e0b', 
                            background: r.status.includes('Approved') || r.status.includes('Offered') ? '#f0fdf4' : '#fffbeb', 
                            fontWeight: 'bold', 
                            padding: '4px 8px', 
                            borderRadius: '4px',
                            border: '1px solid currentColor',
                            fontSize: '11px'
                          }}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Empty RFQ Banner */
              <div className="empty-rfqs-banner">
                <div className="empty-rfqs-image-container">
                  <img src="/assets/images/rfq.png" alt="Empty RFQs" />
                </div>
                <div className="empty-rfqs-details">
                  <h2>Your RFQ is empty!</h2>
                  <p>Explore your favourite items & raise RFQ's</p>
                  <button className="start-shopping-btn" onClick={() => navigate('/')}>
                    Continue Shopping
                  </button>
                </div>
              </div>
            )}

            {/* Trending Categories Section */}
            <div className="rfqs-trending-categories">
              <h3>Trending Categories</h3>
              <div className="trending-categories-grid">
                {trendingCategories.map((cat, idx) => (
                  <div key={idx} className="trending-category-square-card">
                    <div className="trending-img-box">
                      <img src={cat.image} alt={cat.name} />
                    </div>
                    <div className="trending-name-box">
                      <span>{cat.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </main>
        </div>
      </div>
    </div>
  );
};

export default RFQs;
